import React, { useEffect, useRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { MessageItem } from './MessageItem';
import { TypingIndicator } from './TypingIndicator';
import { useMessaging } from '../../contexts/MessagingContext';
import { useAuth } from '../../contexts/AuthContext';
import type { Message } from '../../types/message';

interface MessageListProps {
  conversationId: string;
  onReply?: (messageId: string) => void;
}

/**
 * MessageList Component
 *
 * Displays a list of messages in a conversation with:
 * - Auto-scroll to bottom on new messages
 * - Typing indicators
 * - Grouping of consecutive messages from same sender
 * - Loading state
 */
export const MessageList: React.FC<MessageListProps> = ({ conversationId, onReply }) => {
  const { authState } = useAuth();
  const user = authState.user;
  const { messages, loading } = useMessaging();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const prevMessagesLengthRef = useRef(messages.length);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (messages.length > prevMessagesLengthRef.current) {
      // New message added, scroll to bottom
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevMessagesLengthRef.current = messages.length;
  }, [messages.length]);

  // Initial scroll to bottom
  useEffect(() => {
    if (!loading && messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }
  }, [loading]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (messages.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No messages yet. Start the conversation!
        </Typography>
      </Box>
    );
  }

  // Sort messages by created date (oldest first)
  const sortedMessages = [...messages].sort(
    (a, b) => a.createdAt.getTime() - b.createdAt.getTime()
  );

  // Group messages to determine when to show avatars
  const shouldShowAvatar = (index: number): boolean => {
    if (index === sortedMessages.length - 1) return true;

    const currentMessage = sortedMessages[index];
    const nextMessage = sortedMessages[index + 1];

    // Show avatar if next message is from different sender
    if (currentMessage.senderId !== nextMessage.senderId) return true;

    // Show avatar if there's a significant time gap (> 5 minutes)
    const timeDiff = nextMessage.createdAt.getTime() - currentMessage.createdAt.getTime();
    if (timeDiff > 5 * 60 * 1000) return true;

    return false;
  };

  return (
    <Box
      ref={messagesContainerRef}
      sx={{
        flex: 1,
        overflowY: 'auto',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {sortedMessages.map((message: Message, index: number) => (
        <MessageItem
          key={message.id}
          message={message}
          isOwnMessage={user?.uid === message.senderId}
          showAvatar={shouldShowAvatar(index)}
          onReply={onReply}
        />
      ))}

      {/* Typing Indicator */}
      <TypingIndicator conversationId={conversationId} />

      {/* Scroll anchor */}
      <div ref={messagesEndRef} />
    </Box>
  );
};
